if (!customElements.get('wlsn-slider')) {
  class WLSNSlider extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        slider: 'js-wlsn-slider',
        nextButton: 'js-wlsn-slider-next',
        prevButton: 'js-wlsn-slider-prev',
        sliderId: `${this.getAttribute('data-id')}`,
        sliderSettings: `SliderSettings-${this.getAttribute('data-id')}`,
        pagination: 'js-wlsn-slider-pagination',
        navigation: 'wls-slider__navigation',
        navigationDisabled: 'wlsn-navigation-disabled',
        paginationDisabled: 'wlsn-pagination-disabled',
        locked: 'wlsn-slider-locked',
        autoWidth: '[data-auto-width]',
        syncId: `${this.getAttribute('data-sync-id')}`
      };

      this.selectors = this.getSelectors();

      this.swiperInstance = null;
    }

    /**
     * @returns {Object}
     * @description Get selectors
     */
    getSelectors() {
      return {
        slider: this.querySelector(`.${this.classes.slider}`),
        nextButton: this.querySelector(`.${this.classes.nextButton}`),
        prevButton: this.querySelector(`.${this.classes.prevButton}`),
        pagination: this.querySelector(`.${this.classes.pagination}`),
      };
    }

    /**
     * @description Initialize the slider
     */
    connectedCallback() {
      const totalSlides = this.selectors.slider?.querySelectorAll('.swiper-slide')?.length || 0;
      if (totalSlides >= 2) {
        this.init();
        this.syncWithOtherSliders(this.classes.syncId);
        this.addResizeListener();
      }
    }

    /**
     * @description Add resize listener to handle dynamic slider enabling/disabling
     */
    addResizeListener() {
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.handleResize();
        }, 250);
      };
      
      window.addEventListener('resize', handleResize);
      this.resizeListener = handleResize;
    }

    /**
     * @description Handle resize event by checking if slider should be enabled/disabled
     */
    handleResize() {
      const { enableSlider, spaceBetween } = this.getSliderSettingsFromLiquid();
      const isCurrentlyEnabled = this.isSliderEnabledForCurrentBreakpoint(enableSlider);
      const hasSwiperInstance = !!this.swiperInstance;
      
      if (!isCurrentlyEnabled && hasSwiperInstance) {
        // Slider should be disabled but is currently active
        this.destroy();
        this.disableSliderForCurrentBreakpoint(spaceBetween);
      } else if (isCurrentlyEnabled && !hasSwiperInstance) {
        // Slider should be enabled but is currently disabled
        this.enableSliderForCurrentBreakpoint();
        this.init();
      }
    }

    /**
     * @description Re-enable slider after it was disabled
     */
    enableSliderForCurrentBreakpoint() {
      const slider = this.selectors.slider;
      const wrapper = slider?.querySelector('.wlsn-slider__wrapper');
      const slides = wrapper?.querySelectorAll('.wlsn-slider__slide');
      
      if (slider) {
        slider.classList.add('swiper');
        slider.classList.remove('wlsn-slider-disabled');
      }
      
      if (wrapper) {
        wrapper.classList.add('swiper-wrapper');
        wrapper.classList.remove('wlsn-slider__wrapper');
        wrapper.style.removeProperty('--wlsn-disabled-slider-gap');
      }
      
      if (slides) {
        slides.forEach(slide => {
          slide.classList.add('swiper-slide');
          slide.classList.remove('wlsn-slider__slide');
        });
      }
    }

    /**
     * @description Destroy the slider
     */
    disconnectedCallback() {
      if (this.resizeListener) {
        window.removeEventListener('resize', this.resizeListener);
      }
      if (this.swiperInstance) {
        this.destroy();
      }
    }

    /**
     * @description Initialize the slider
     */
    init() {
      const totalSlides = this.selectors.slider?.querySelectorAll('.swiper-slide')?.length || 0;
      const {customSettings, slidesPerView, loop, centeredSlides, spaceBetween, navigation, pagination, paginationType, enableSlider } =
        this.getSliderSettingsFromLiquid();

      // Check if slider should be enabled for current breakpoint
      const isEnabledForCurrentBreakpoint = this.isSliderEnabledForCurrentBreakpoint(enableSlider);
      
      if (!isEnabledForCurrentBreakpoint) {
        this.disableSliderForCurrentBreakpoint(spaceBetween);
        return;
      }

      const shouldCenterSlides = {
        base: totalSlides > slidesPerView.base + 2,
        tablet: totalSlides >= slidesPerView.tablet + 2,
        desktop: totalSlides >= slidesPerView.desktop + 2,
      };

      const resolveLoop = (configValue, slidesPerViewValue) => {
        if (slidesPerViewValue === 'auto') return configValue;
        return configValue ? totalSlides > slidesPerViewValue : false;
      };

      this.swiperInstance = new Swiper(this.selectors.slider, {
        slidesPerView: slidesPerView.base ?? 1.1,
        spaceBetween: spaceBetween.base ?? 16,
        loop: resolveLoop(loop.base, slidesPerView.base ?? 1.1),
        centeredSlides: centeredSlides.base ? shouldCenterSlides.base : false,
        noSwipingClass: 'wlsn-btn',
        ...customSettings,
        navigation: {
          nextEl: this.selectors.nextButton,
          prevEl: this.selectors.prevButton,
          enabled: navigation?.base ?? false,
        },
        pagination: {
          el: this.selectors.pagination,
          type: paginationType?.base ?? 'progressbar',
          enabled: pagination?.base ?? false,
          clickable: paginationType?.base === 'bullets',
        },
        on: {
          init: (swiper) => {
            this.updateNavigationPaginationClasses(swiper);
          },
          breakpoint: this.updateNavigationPaginationClasses.bind(this),
          lock: this.updateNavigationPaginationClasses.bind(this),
          unlock: this.updateNavigationPaginationClasses.bind(this),
          slideChange: this.handleSlideChange.bind(this),
        },
        breakpoints: {
          768: {
            slidesPerView: slidesPerView.tablet ?? 1.7,
            spaceBetween: spaceBetween.tablet ?? 16,
            loop: resolveLoop(loop.tablet, slidesPerView.tablet ?? 1.7),
            centeredSlides: centeredSlides.tablet ? shouldCenterSlides.tablet : false,
            navigation: {
              enabled: navigation?.tablet ?? false,
            },
            pagination: {
              enabled: pagination?.tablet ?? false,
            },
          },
          1024: {
            slidesPerView: slidesPerView.desktop ?? 3,
            spaceBetween: spaceBetween.desktop ?? 24,
            loop: resolveLoop(loop.desktop, slidesPerView.desktop ?? 3),
            centeredSlides: centeredSlides.desktop ? shouldCenterSlides.desktop : false,
            navigation: {
              enabled: navigation?.desktop ?? false,
            },
            pagination: {
              enabled: pagination?.desktop ?? false,
            },
          },
        },
      });
    }

    /**
     * @param {Swiper} swiper
     * @description Update the navigation and pagination classes
     */
    updateNavigationPaginationClasses(swiper) {
      if (!swiper) return;

      const { navigation, pagination, isLocked, params } = swiper;
      if (!params) return;

      const { navigation: navigationParams, pagination: paginationParams } = params;
      if (!navigationParams || !paginationParams) return;

      const navigationEnabled = navigationParams.enabled;
      const paginationEnabled = paginationParams.enabled;

      if (navigation && !navigationEnabled) {
        this.classList.add(this.classes.navigationDisabled);
      } else {
        this.classList.remove(this.classes.navigationDisabled);
      }

      if (pagination && !paginationEnabled) {
        this.classList.add(this.classes.paginationDisabled);
      } else {
        this.classList.remove(this.classes.paginationDisabled);
      }

      if (isLocked) {
        this.classList.add(this.classes.locked);
      } else {
        this.classList.remove(this.classes.locked);
      }
    }

    syncWithOtherSliders(syncId) {
      if (!syncId) return;
    
      const syncedElements = document.querySelectorAll(`wlsn-slider[data-sync-id="${syncId}"]`);
      syncedElements.forEach((el) => {
        const otherSwiper = el.querySelector('.js-wlsn-slider')?.swiper;
        if (otherSwiper && otherSwiper !== this.swiperInstance) {
          this.swiperInstance.controller.control = otherSwiper;
          otherSwiper.controller.control = this.swiperInstance;
        }
      });
    }

    /**
     * @description Handle slide change event by pausing all videos
     */
    handleSlideChange() {
      const videos = this.querySelectorAll('wlsn-video');
      videos.forEach((videoElement) => {
        const video = videoElement.querySelector('video');
        if (video && !video.hasAttribute('autoplay') && !video.paused) {
          videoElement.classList.remove('playing');
          video.pause();
        }
      });
    }

    /**
     * @description Destroy the slider
     */
    destroy() {
      this.swiperInstance.destroy();
    }

    /**
     * @returns {Object}
     * @description Get slider settings from liquid
     */
    getSliderSettingsFromLiquid() {
      const settingsElement = document.getElementById(`${this.classes.sliderSettings}`);
      const settings = JSON.parse(settingsElement.textContent);
      return settings;
    }

    /**
     * @param {Object} enableSlider
     * @returns {boolean}
     * @description Check if slider should be enabled for current breakpoint
     */
    isSliderEnabledForCurrentBreakpoint(enableSlider) {
      if (!enableSlider) return true; // Default to enabled if no settings
      
      const width = window.innerWidth;
      
      if (width >= 1024) {
        return enableSlider.desktop !== false; // Default to true if not explicitly set
      } else if (width >= 768) {
        return enableSlider.tablet !== false; // Default to true if not explicitly set
      } else {
        return enableSlider.base !== false; // Default to true if not explicitly set
      }
    }

    /**
     * @param {Object} spaceBetween
     * @description Disable slider for current breakpoint by removing slider classes and making slides display normally
     */
    disableSliderForCurrentBreakpoint(spaceBetween) {
      const slider = this.selectors.slider;
      const wrapper = slider?.querySelector('.swiper-wrapper');
      const slides = slider?.querySelectorAll('.swiper-slide');
      
      if (slider) {
        slider.classList.remove('swiper');
        slider.classList.add('wlsn-slider-disabled');
      }
      
      if (wrapper) {
        wrapper.classList.remove('swiper-wrapper');
        wrapper.classList.add('wlsn-slider__wrapper');
        
        // Set gap based on space between slides setting for current breakpoint
        const currentGap = this.getCurrentBreakpointSpaceBetween(spaceBetween);
        wrapper.style.setProperty('--wlsn-disabled-slider-gap', `${currentGap}px`);
      }
      
      if (slides) {
        slides.forEach(slide => {
          slide.classList.remove('swiper-slide');
          slide.classList.add('wlsn-slider__slide');
        });
      }
    }

    /**
     * @param {Object} spaceBetween
     * @returns {number}
     * @description Get the space between slides value for the current breakpoint
     */
    getCurrentBreakpointSpaceBetween(spaceBetween) {
      if (!spaceBetween) return 16; // Default fallback
      
      const width = window.innerWidth;
      
      if (width >= 1024) {
        return spaceBetween.desktop || 24;
      } else if (width >= 768) {
        return spaceBetween.tablet || 24;
      } else {
        return spaceBetween.base || 16;
      }
    }
  }
  customElements.define('wlsn-slider', WLSNSlider);
}
